import type { NavigateWithSearchOptions } from "@lending/hooks/useNavigateWithSearch";
import type { ProductType } from "@lending/navigation";

type GoToPath = (path: string, options?: NavigateWithSearchOptions) => void;

interface NavigateToMembershipShareStartStepArgs {
  applicationType: ProductType;
  lendingApplicationID: string;
  state: string;
  verificationRequired: boolean;
  emailConflict?: boolean;
  goToPath: GoToPath;
}

/**
 * Sends the applicant to the correct starting step for the membership-share flow.
 *
 * Membership share screens only exist in the web flow, so we always go there, skipping the
 * usual rewrite to the matching `olb-` route. Applications from online banking never enter
 * membership share. Standalone Loan Portal launches reuse the OLB screens but aren't online
 * banking applications, so they can end up here.
 */
function navigateToMembershipShareStartStep({
  applicationType,
  lendingApplicationID,
  state,
  verificationRequired,
  emailConflict = false,
  goToPath,
}: NavigateToMembershipShareStartStepArgs): Promise<void> {
  const membershipShareBasePath = `/${applicationType}/${lendingApplicationID}/membership-share`;

  if (emailConflict) {
    goToPath(`${membershipShareBasePath}/use-different-email`, { skipOlbRewrite: true });
    return Promise.resolve();
  }

  const fallbackPath =
    state === "membership_selection"
      ? `${membershipShareBasePath}/select`
      : `${membershipShareBasePath}/activate`;

  if (!verificationRequired) {
    goToPath(fallbackPath, { skipOlbRewrite: true });
    return Promise.resolve();
  }

  goToPath(`${membershipShareBasePath}/verify-email`, { skipOlbRewrite: true });
  return Promise.resolve();
}

export { navigateToMembershipShareStartStep };
